// ============================================
// LOGIN.JS — Autenticación Beep OS + Expo Alianza
// ============================================
import React, { useState, useEffect } from 'react';
import { STORAGE_KEYS, storage } from './utils/storage';
// FIX-SESION-OBSERVABILIDAD: avisar al usuario cuando viene redirigido por sesion expirada.
import { consumirFlagSesionExpiro } from './utils/sesion';
import { API_BASE } from './config/api';

const PP = "'Poppins', sans-serif";
const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)
const C_BEEP = { azul: '#5c9adb', osc: '#2f5f9e', verde: '#5cd4b0', rojo: '#e05c5c', fondo: '#f3f4f4', gris: '#5e666e' };
const C_EXPO = { principal: '#270f2b', acento: '#94b803', card: '#3d1540', morado: '#672369' };

const EMPRESAS_RAPIDAS = [
  'Comercializadora Beep',
  'Cesar Armenta',
  'Fernando Bernal',
  'Eliud Ríos',
];

// ENCAPSULAR-TUEXPO (2026-10-15): el boton "Acceder a TuExpo Alianza" del login de Beep no se enseña a nadie.
// El modo expo de esta pantalla (login rapido) queda sin usar a proposito: no se borra, por si se reabre.
const MOSTRAR_TUEXPO = false;

export default function Login({ onLogin, modo: modoProp }) {
  const esExpo = modoProp === 'expo' || window.location.pathname === '/expo';

  // Beep OS login state
  // SEGURIDAD SPRINT 1 (2026-07-02): se elimino el modo "registro" (el
  // endpoint publico /api/auth/registro fue borrado del backend; las cuentas
  // se crean solo desde la pantalla Usuarios).
  const [correo, setCorreo] = useState('');
  // PRIMER-ACCESO (2026-10-05): si la cuenta tiene una contraseña temporal, el servidor
  // no da el token de trabajo sino un pase de 15 min para crear la propia.
  const [primerAcceso, setPrimerAcceso] = useState(null); // { pase, nombre }
  // CONTRATISTAS-EXTERNOS Bloque 2: la cuenta externa pide el codigo de su aplicacion (TOTP) tras la contraseña.
  const [dosFactores, setDosFactores] = useState(null); // { pase2fa }
  const [codigo2fa, setCodigo2fa] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [password, setPassword] = useState('');
  const [verPassword, setVerPassword] = useState(false);

  // Expo login state
  const [nombreExpo, setNombreExpo] = useState('');
  const [empresaExpo, setEmpresaExpo] = useState('');
  const [empresaOtra, setEmpresaOtra] = useState('');
  const [expoConfig, setExpoConfig] = useState({ logoUrl: '', nombreEvento: 'Expo Alianza', anoEvento: '2026' });

  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [cargando, setCargando] = useState(false);

  // FIX-SESION-OBSERVABILIDAD: si el usuario fue redirigido aqui por sesion
  // expirada (flag en sessionStorage), mostrar aviso amarillo encima del form.
  useEffect(() => {
    if (consumirFlagSesionExpiro()) {
      setAviso('Tu sesion expiro por seguridad. Vuelve a entrar.');
    }
  }, []);

  // Cargar config del evento para el login de expo
  useEffect(() => {
    if (esExpo) {
      fetch(`${API}/api/expo/config`).then(r => r.json())
        .then(d => { if (d.config) setExpoConfig(d.config); })
        .catch(() => {});
    }
  }, [esExpo]);

  // ─── EXPO LOGIN ─────────────────────────────────
  const handleExpoLogin = async (e) => {
    e.preventDefault();
    setError('');
    const emp = empresaExpo === '__otra' ? empresaOtra : empresaExpo;
    if (!nombreExpo.trim() || !emp.trim()) { setError('Nombre y empresa son requeridos'); return; }
    setCargando(true);
    try {
      const res = await fetch(`${API}/api/expo/login-rapido`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre: nombreExpo.trim(), empresa: emp.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error al conectar'); setCargando(false); return; }
      storage.set(STORAGE_KEYS.AUTH_TOKEN, data.token);
      storage.set(STORAGE_KEYS.USER_DATA, data.usuario);
      if (onLogin) onLogin(data.usuario);
    } catch (err) { setError('No se pudo conectar al servidor'); }
    setCargando(false);
  };

  // ─── BEEP OS LOGIN ──────────────────────────────
  const handleBeepLogin = async (e) => {
    e.preventDefault();
    setError('');
    setCargando(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correo, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error de autenticación'); setCargando(false); return; }
      if (data.debeCambiarPassword) {
        setPrimerAcceso({ pase: data.pase, nombre: (data.usuario && data.usuario.nombre) || '' });
        setPassword('');
        setCargando(false);
        return;
      }
      if (data.requiere2fa) { setDosFactores({ pase2fa: data.pase2fa }); setPassword(''); setCargando(false); return; }
      storage.set(STORAGE_KEYS.AUTH_TOKEN, data.token);
      storage.set(STORAGE_KEYS.USER_DATA, data.usuario);
      if (onLogin) onLogin(data.usuario);
    } catch (err) { setError('No se pudo conectar al servidor. Intenta de nuevo.'); }
    setCargando(false);
  };

  // ─── 2FA (cuenta externa): codigo de la aplicacion o codigo de recuperacion ──
  const handle2fa = async (e) => {
    e.preventDefault(); setError(''); setCargando(true);
    try {
      const res = await fetch(`${API}/api/auth/2fa`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pase2fa: dosFactores.pase2fa, codigo: codigo2fa }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Codigo incorrecto'); setCargando(false); return; }
      if (data.via === 'recuperacion') setAviso(`Entraste con un codigo de recuperacion. Te quedan ${data.codigos_restantes}.`);
      storage.set(STORAGE_KEYS.AUTH_TOKEN, data.token);
      storage.set(STORAGE_KEYS.USER_DATA, data.usuario);
      if (onLogin) onLogin(data.usuario);
    } catch (err) { setError('No se pudo conectar al servidor. Intenta de nuevo.'); }
    setCargando(false);
  };
  const pedirReseteo2fa = async () => {
    if (!window.confirm('¿Pedir a Beep Mexico que resetee tu segundo factor? Solo hazlo si perdiste el telefono Y los codigos de recuperacion.')) return;
    setError(''); setCargando(true);
    try {
      const res = await fetch(`${API}/api/auth/2fa/solicitar-reseteo`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pase2fa: dosFactores.pase2fa }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'No se pudo enviar la solicitud'); } else { setAviso(data.mensaje); setDosFactores(null); setCodigo2fa(''); }
    } catch (err) { setError('No se pudo conectar al servidor. Intenta de nuevo.'); }
    setCargando(false);
  };

  // ─── PRIMER ACCESO: crear la contraseña propia ──
  const nuevaValida = nueva.length >= 8 && /[A-Z]/.test(nueva) && /[0-9]/.test(nueva);
  const handlePrimerAcceso = async (e) => {
    e.preventDefault();
    setError('');
    if (!nuevaValida) { setError('Necesita 8 caracteres, una mayúscula y un número'); return; }
    if (nueva !== confirmar) { setError('Las dos contraseñas no coinciden'); return; }
    setCargando(true);
    try {
      const res = await fetch(`${API}/api/auth/primer-acceso`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pase: primerAcceso.pase, password_nueva: nueva }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'No se pudo guardar la contraseña');
        if (res.status === 401 || res.status === 409) { setPrimerAcceso(null); setNueva(''); setConfirmar(''); }
        setCargando(false);
        return;
      }
      storage.set(STORAGE_KEYS.AUTH_TOKEN, data.token);
      storage.set(STORAGE_KEYS.USER_DATA, data.usuario);
      if (onLogin) onLogin(data.usuario);
    } catch (err) { setError('No se pudo conectar al servidor. Intenta de nuevo.'); }
    setCargando(false);
  };

  const inp = {
    width: '100%', padding: '14px 16px', borderRadius: 12, fontSize: 16,
    boxSizing: 'border-box', outline: 'none', fontFamily: PP, WebkitAppearance: 'none',
  };
  const inpBeep = { ...inp, background: '#f3f4f4', border: '1px solid #d0d0d0', color: '#333' };
  const inpExpo = { ...inp, background: C_EXPO.card, border: '1px solid rgba(148,184,3,0.3)', color: '#fff' };
  const lblBeep = { color: '#4e5357', fontSize: 13, display: 'block', marginBottom: 6, fontWeight: 500 };
  const lblExpo = { color: 'rgba(255,255,255,0.7)', fontSize: 13, display: 'block', marginBottom: 6, fontWeight: 500 };

  // ═══════════════════════════════════════
  // RENDER: EXPO LOGIN (/expo)
  // ═══════════════════════════════════════
  if (esExpo) {
    return (
      <div style={{
        minHeight: '100dvh', background: C_EXPO.principal,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: PP, padding: 16, boxSizing: 'border-box',
      }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          {/* Logo del evento */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{
              width: 80, height: 80, borderRadius: 20, background: C_EXPO.card,
              border: '2px solid rgba(148,184,3,0.3)', margin: '0 auto 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}>
              {expoConfig.logoUrl
                ? <img src={expoConfig.logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                : <span style={{ fontSize: 36 }}>🎪</span>
              }
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C_EXPO.acento, letterSpacing: 2 }}>
              {(expoConfig.nombreEvento || 'EXPO ALIANZA').toUpperCase()}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', letterSpacing: 3, marginTop: 2 }}>
              {expoConfig.anoEvento || '2026'}
            </div>
          </div>

          <form onSubmit={handleExpoLogin}>
            {/* Nombre completo */}
            <div style={{ marginBottom: 14 }}>
              <label style={lblExpo}>Nombre completo</label>
              <input value={nombreExpo} onChange={e => setNombreExpo(e.target.value)}
                placeholder="Nombre y apellido" required style={inpExpo} />
            </div>

            {/* Selector de empresa (pills) */}
            <div style={{ marginBottom: 14 }}>
              <label style={lblExpo}>Empresa</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {EMPRESAS_RAPIDAS.map(emp => (
                  <button key={emp} type="button" onClick={() => setEmpresaExpo(emp)} style={{
                    padding: '8px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily: PP,
                    background: empresaExpo === emp ? C_EXPO.acento : C_EXPO.card,
                    color: empresaExpo === emp ? C_EXPO.principal : 'rgba(255,255,255,0.6)',
                    fontSize: 12, fontWeight: 600,
                    transition: 'all 0.15s',
                  }}>{emp}</button>
                ))}
                <button type="button" onClick={() => setEmpresaExpo('__otra')} style={{
                  padding: '8px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily: PP,
                  background: empresaExpo === '__otra' ? C_EXPO.acento : C_EXPO.card,
                  color: empresaExpo === '__otra' ? C_EXPO.principal : 'rgba(255,255,255,0.6)',
                  fontSize: 12, fontWeight: 600,
                  transition: 'all 0.15s',
                }}>Otra</button>
              </div>
              {empresaExpo === '__otra' && (
                <input value={empresaOtra} onChange={e => setEmpresaOtra(e.target.value)}
                  placeholder="Nombre de tu empresa" required style={inpExpo} />
              )}
            </div>

            {aviso && (
              <div style={{
                background: 'rgba(252,211,77,0.15)', color: '#fcd34d',
                padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 14, fontWeight: 500,
                border: '1px solid rgba(252,211,77,0.35)',
              }}>{aviso}</div>
            )}

            {error && (
              <div style={{
                background: 'rgba(255,23,68,0.15)', color: '#ff6b6b',
                padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 14, fontWeight: 500,
              }}>{error}</div>
            )}

            <button type="submit" disabled={cargando} style={{
              width: '100%', padding: 18, borderRadius: 14, border: 'none',
              background: cargando ? '#555' : C_EXPO.acento,
              color: cargando ? '#999' : C_EXPO.principal,
              fontSize: 17, fontWeight: 800, cursor: cargando ? 'wait' : 'pointer', fontFamily: PP,
              letterSpacing: 1,
            }}>
              {cargando ? 'Conectando...' : 'ENTRAR AL MONTAJE'}
            </button>
          </form>

          {/* Link a Beep OS */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button onClick={() => { window.location.href = '/beep-login'; }} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
              fontSize: 12, cursor: 'pointer', fontFamily: PP,
            }}>
              ← Acceder a Beep OS
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════
  // RENDER: BEEP OS LOGIN (/)
  // ═══════════════════════════════════════
  return (
    <div style={{
      minHeight: '100vh', minHeight: '100dvh', background: C_BEEP.fondo,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: PP, padding: 16, boxSizing: 'border-box',
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: '32px 28px',
        width: '100%', maxWidth: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        {/* Logo Beep */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/Logo Original Cuadrado.png" alt="BeepMX" style={{
            width: 100, height: 100, margin: '0 auto 10px', display: 'block',
          }} />
          <div style={{ fontSize: 22, fontWeight: 800, color: C_BEEP.osc }}>Beep OS</div>
          <p style={{ color: C_BEEP.gris, fontSize: 13, margin: '4px 0 0' }}>Sistema operativo de Beep México</p>
        </div>

        {dosFactores ? (
          <form onSubmit={handle2fa}>
            <p style={{ color: '#4e5357', fontSize: 14, margin: '0 0 16px', lineHeight: 1.5 }}>
              Escribe el codigo de 6 digitos de tu aplicacion de codigos (o uno de tus codigos de recuperacion).
            </p>
            <div style={{ marginBottom: 14 }}>
              <label style={lblBeep}>Codigo</label>
              <input value={codigo2fa} onChange={e => setCodigo2fa(e.target.value)} style={{ ...inpBeep, fontSize: 20, letterSpacing: 4, textAlign: 'center', fontFamily: 'monospace' }}
                inputMode="text" autoComplete="one-time-code" autoFocus required maxLength={12} />
            </div>
            {aviso && (<div style={{ background: '#FEF3C7', color: '#92400E', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 14, fontWeight: 500, border: '1px solid #FDE68A' }}>{aviso}</div>)}
            {error && (<div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 14, fontWeight: 500 }}>{error}</div>)}
            <button type="submit" disabled={cargando} style={{ width: '100%', padding: 14, background: cargando ? '#aaa' : C_BEEP.osc, color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: cargando ? 'wait' : 'pointer', fontFamily: PP }}>
              {cargando ? 'Comprobando...' : 'Entrar'}
            </button>
            <button type="button" onClick={pedirReseteo2fa} disabled={cargando} style={{ width: '100%', marginTop: 10, padding: 10, background: 'none', border: 'none', color: C_BEEP.gris, fontSize: 13, cursor: 'pointer', fontFamily: PP }}>Perdi mi telefono y mis codigos</button>
            <button type="button" onClick={() => { setDosFactores(null); setCodigo2fa(''); setError(''); }} style={{ width: '100%', padding: 10, background: 'none', border: 'none', color: C_BEEP.gris, fontSize: 13, cursor: 'pointer', fontFamily: PP }}>Volver</button>
          </form>
        ) : primerAcceso ? (
          <form onSubmit={handlePrimerAcceso}>
            <p style={{ color: '#4e5357', fontSize: 14, margin: '0 0 16px', lineHeight: 1.5 }}>
              Hola{primerAcceso.nombre ? `, ${primerAcceso.nombre.split(' ')[0]}` : ''}. Entraste con una contraseña temporal:
              crea la tuya para seguir.
            </p>
            <div style={{ marginBottom: 14 }}>
              <label style={lblBeep}>Contraseña nueva</label>
              <input type="password" value={nueva} onChange={e => setNueva(e.target.value)} style={inpBeep}
                autoComplete="new-password" autoFocus required />
              <div style={{ fontSize: 12, color: nuevaValida ? '#2e7d5b' : C_BEEP.gris, marginTop: 6 }}>
                8 caracteres o más, con una mayúscula y un número
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lblBeep}>Repítela</label>
              <input type="password" value={confirmar} onChange={e => setConfirmar(e.target.value)} style={inpBeep}
                autoComplete="new-password" required />
            </div>
            {error && (
              <div style={{
                background: '#FEE2E2', color: '#991B1B',
                padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 14, fontWeight: 500,
              }}>{error}</div>
            )}
            <button type="submit" disabled={cargando} style={{
              width: '100%', padding: 14,
              background: cargando ? '#aaa' : C_BEEP.osc,
              color: '#fff', border: 'none', borderRadius: 12,
              fontSize: 16, fontWeight: 600, cursor: cargando ? 'wait' : 'pointer', fontFamily: PP,
            }}>
              {cargando ? 'Guardando...' : 'Guardar y entrar'}
            </button>
            <button type="button" onClick={() => { setPrimerAcceso(null); setNueva(''); setConfirmar(''); setError(''); }} style={{
              width: '100%', marginTop: 10, padding: 10, background: 'none', border: 'none',
              color: C_BEEP.gris, fontSize: 13, cursor: 'pointer', fontFamily: PP,
            }}>Volver</button>
          </form>
        ) : (
        <form onSubmit={handleBeepLogin}>
          <div style={{ marginBottom: 14 }}>
            <label style={lblBeep}>Correo</label>
            <input type="email" value={correo} onChange={e => setCorreo(e.target.value)}
              placeholder="tu@beepmx.com" required style={inpBeep} />
          </div>

          <div style={{ marginBottom: 20, position: 'relative' }}>
            <label style={lblBeep}>Contraseña</label>
            <input type={verPassword ? 'text' : 'password'} value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••" required style={inpBeep} />
            <button type="button" onClick={() => setVerPassword(v => !v)} style={{
              position: 'absolute', right: 12, top: 34,
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 18, color: C_BEEP.gris, padding: 4,
            }}>{verPassword ? '🙈' : '👁️'}</button>
          </div>

          {aviso && (
            <div style={{
              background: '#FEF3C7', color: '#92400E',
              padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 14, fontWeight: 500,
              border: '1px solid #FDE68A',
            }}>{aviso}</div>
          )}

          {error && (
            <div style={{
              background: '#FEE2E2', color: '#991B1B',
              padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 14, fontWeight: 500,
            }}>{error}</div>
          )}

          <button type="submit" disabled={cargando} style={{
            width: '100%', padding: 14,
            background: cargando ? '#aaa' : C_BEEP.osc,
            color: '#fff', border: 'none', borderRadius: 12,
            fontSize: 16, fontWeight: 600, cursor: cargando ? 'wait' : 'pointer', fontFamily: PP,
          }}>
            {cargando ? 'Conectando...' : 'Entrar a Beep OS'}
          </button>
        </form>
        )}

        {/* Botón TuExpo Alianza (ENCAPSULAR-TUEXPO: oculto para todos, ver MOSTRAR_TUEXPO arriba) */}
        {MOSTRAR_TUEXPO && (
        <button onClick={() => { window.location.href = '/expo'; }} style={{
          width: '100%', marginTop: 20, padding: 14, borderRadius: 12,
          background: C_EXPO.principal, border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontFamily: PP, transition: 'opacity 0.15s',
        }}>
          <span style={{ fontSize: 20 }}>🎪</span>
          <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>Acceder a TuExpo Alianza</span>
          <span style={{ color: C_EXPO.acento, fontSize: 14 }}>→</span>
        </button>
        )}

        <div style={{
          marginTop: 16, padding: '10px 14px', background: C_BEEP.fondo,
          borderRadius: 8, borderLeft: '3px solid #dcde74',
        }}>
          <p style={{ margin: 0, fontSize: 12, color: C_BEEP.gris }}>
            ¿Problemas para entrar? Contacta a tu administrador.
          </p>
        </div>

        {/* Aviso de privacidad: enlace discreto, publico (/privacidad) */}
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <a href="/privacidad" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: C_BEEP.gris, textDecoration: 'underline' }}>
            Aviso de privacidad
          </a>
        </div>
      </div>
    </div>
  );
}
